import { useEffect, useRef, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  mediaUrl,
  PORTAL_FONT_PRESETS,
  TRAINER_COLOR_PRESETS,
  buildClientHeroTheme,
  heroBackgroundStyle,
} from "../../../utils/clientHeroTheme";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { Button, useToast } from "../../ui";

export default function SettingsPortalBrandingPanel({
  accentColor,
  coverUrl,
  logoUrl,
  fontFamily,
  galleryUrls = [],
  onUpdated,
  compact = false,
  rootClassName = "",
}) {
  const toast = useToast();
  const coverInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [color, setColor] = useState(accentColor || "#6d28d9");
  const [font, setFont] = useState(fontFamily || "inter");
  const [cover, setCover] = useState(coverUrl || "");
  const [logo, setLogo] = useState(logoUrl || "");
  const [gallery, setGallery] = useState(galleryUrls || []);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const syncFromProps = (nextAccent, nextCover, nextLogo, nextFont, nextGallery) => {
    if (nextAccent !== undefined) setColor(nextAccent || "#6d28d9");
    if (nextCover !== undefined) setCover(nextCover || "");
    if (nextLogo !== undefined) setLogo(nextLogo || "");
    if (nextFont !== undefined) setFont(nextFont || "inter");
    if (nextGallery !== undefined) setGallery(nextGallery || []);
  };

  useEffect(() => {
    syncFromProps(accentColor, coverUrl, logoUrl, fontFamily, galleryUrls);
  }, [accentColor, coverUrl, logoUrl, fontFamily, galleryUrls]);

  const patch = async (fields) => {
    setSaving(true);
    try {
      const res = await clinicAPI.updateTenantSettings(fields);
      const t = res.tenant;
      syncFromProps(
        t.portal_accent_color,
        t.portal_cover_url,
        t.logo_url,
        t.portal_font_family,
        t.portal_gallery,
      );
      onUpdated?.(t);
      return t;
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveColor = async (nextColor) => {
    setColor(nextColor);
    const t = await patch({ portal_accent_color: nextColor });
    if (t) toast.success("Цвет сохранён");
  };

  const saveFont = async (nextFont) => {
    setFont(nextFont);
    const t = await patch({ portal_font_family: nextFont });
    if (t) toast.success("Шрифт сохранён");
  };

  const uploadCoverFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Допустимы JPEG, PNG или WebP");
      return;
    }
    setCoverUploading(true);
    try {
      const res = await clinicAPI.uploadTenantPortalCover(file);
      const raw = res.portal_cover_url || res.tenant?.portal_cover_url;
      const url =
        raw && !/\?v=\d+/.test(raw) ? `${raw}?v=${Date.now()}` : raw;
      setCover(url || "");
      onUpdated?.({
        ...res.tenant,
        portal_cover_url: url || res.tenant?.portal_cover_url || null,
      });
      toast.success("Обложка установлена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setCoverUploading(false);
    }
  };

  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadCoverFile(file);
  };

  const uploadLogoFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Допустимы JPEG, PNG или WebP");
      return;
    }
    setLogoUploading(true);
    try {
      const res = await clinicAPI.uploadTenantPortalLogo(file);
      const raw = res.logo_url || res.tenant?.logo_url;
      const url =
        raw && !/\?v=\d+/.test(raw) ? `${raw}?v=${Date.now()}` : raw;
      setLogo(url || "");
      onUpdated?.({
        ...res.tenant,
        logo_url: url || res.tenant?.logo_url || null,
      });
      toast.success("Аватар клуба установлен");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLogoUploading(false);
    }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadLogoFile(file);
  };

  const removeLogo = async () => {
    const t = await patch({ logo_url: "" });
    if (t) toast.success("Аватар убран");
  };

  const openLogoPicker = () => {
    if (!logoUploading) logoInputRef.current?.click();
  };

  const removeCover = async () => {
    const t = await patch({ portal_cover_url: "" });
    if (t) toast.success("Обложка убрана");
  };

  const previewTheme = buildClientHeroTheme({
    portal_accent_color: color,
    portal_cover_url: cover,
    portal_font_family: font,
  });
  const coverPreview = mediaUrl(cover);
  const logoPreview = mediaUrl(logo);

  const previewStyle = heroBackgroundStyle(previewTheme);

  const openCoverPicker = () => {
    if (!coverUploading) coverInputRef.current?.click();
  };

  const openGalleryPicker = () => {
    if (!galleryUploading) galleryInputRef.current?.click();
  };

  const onGalleryPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Допустимы JPEG, PNG или WebP");
      return;
    }
    setGalleryUploading(true);
    try {
      const res = await clinicAPI.uploadTenantPortalGallery(file);
      setGallery(res.portal_gallery || []);
      onUpdated?.(res.tenant);
      toast.success("Фото добавлено в галерею клуба");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeGalleryPhoto = async (url) => {
    const next = gallery.filter((item) => item !== url);
    const t = await patch({ portal_gallery: next });
    if (t) {
      setGallery(next);
      toast.success("Фото удалено");
    }
  };

  const galleryInput = (
    <input
      ref={galleryInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/*"
      hidden
      onChange={onGalleryPick}
    />
  );

  const gallerySection = (
    <section className={aa.portalBrandBlock}>
      <p className={aa.hubSectionLabel}>Фото клуба</p>
      <p className={aa.hubHint}>
        Слайдер на странице «Клуб» у клиентов. Обложка баннера в слайдер не
        попадает — только логотип и загруженные фото.
      </p>
      {galleryInput}
      <div className={aa.portalGalleryGrid}>
        {gallery.map((url) => (
          <div key={url} className={aa.portalGalleryItem}>
            <img src={mediaUrl(url)} alt="" />
            <button
              type="button"
              className={aa.portalGalleryRemove}
              onClick={() => removeGalleryPhoto(url)}
              aria-label="Удалить фото"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className={aa.portalGalleryAdd}
          onClick={openGalleryPicker}
          disabled={galleryUploading}
        >
          {galleryUploading ? "…" : "+ Фото"}
        </button>
      </div>
    </section>
  );

  const coverInput = (
    <input
      ref={coverInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/*"
      hidden
      onChange={onCoverPick}
    />
  );

  const logoInput = (
    <input
      ref={logoInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/*"
      hidden
      onChange={onLogoPick}
    />
  );

  const logoSection = (
    <section className={aa.portalCoverBlock}>
      <p className={aa.hubSectionLabel}>Аватар клуба</p>
      {logoInput}
      <div className={aa.portalLogoRow}>
        <button
          type="button"
          className={aa.portalLogoTap}
          onClick={openLogoPicker}
          disabled={logoUploading}
          aria-label={logoPreview ? "Изменить аватар" : "Добавить аватар"}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="" className={aa.portalLogoImg} />
          ) : (
            <span className={aa.portalLogoPlaceholder}>+</span>
          )}
        </button>
        <div className={aa.portalCoverActions}>
          <Button
            type="button"
            block
            className={aa.hubPrimaryCta}
            disabled={logoUploading}
            onClick={openLogoPicker}
          >
            {logoUploading
              ? "Загрузка…"
              : logoPreview
                ? "Заменить аватар"
                : "Загрузить аватар"}
          </Button>
          {logoPreview ? (
            <button
              type="button"
              className={aa.portalCoverRemoveBtn}
              onClick={removeLogo}
              disabled={saving || logoUploading}
            >
              Убрать аватар
            </button>
          ) : (
            <p className={aa.hubHint}>
              Квадратное фото — логотип в шапке и навигации.
            </p>
          )}
        </div>
      </div>
    </section>
  );

  if (compact) {
    return (
      <div className={aa.portalBrandPanel}>
        <p className={aa.hubHint}>
          Фирменный стиль клуба: шапка на главной админа и клиентов, вкладки,
          кнопки и шрифт. Один раз настроили — видят все сотрудники и клиенты.
        </p>

        {logoSection}

        <section className={aa.portalCoverBlock}>
          <p className={aa.hubSectionLabel}>Фон шапки (обложка)</p>
          {coverInput}
          <button
            type="button"
            className={aa.portalCoverTap}
            style={{ ...previewStyle, fontFamily: previewTheme.fontFamily }}
            onClick={openCoverPicker}
            disabled={coverUploading}
            aria-label={coverPreview ? "Изменить обложку" : "Добавить обложку"}
          >
            <div className={aa.portalBrandPreviewInner}>
              <span className={aa.portalBrandPreviewDate}>Сегодня</span>
              <strong className={aa.portalBrandPreviewTitle}>Привет!</strong>
              <span className={aa.portalBrandPreviewClub}>Ваш клуб</span>
            </div>
            <span className={aa.portalCoverTapBadge}>
              {coverUploading
                ? "Загрузка…"
                : coverPreview
                  ? "Изменить фото"
                  : "+ Добавить фото"}
            </span>
          </button>
          <div className={aa.portalCoverActions}>
            <Button
              type="button"
              block
              className={aa.hubPrimaryCta}
              disabled={coverUploading}
              onClick={openCoverPicker}
            >
              {coverUploading
                ? "Загрузка…"
                : coverPreview
                  ? "Заменить обложку"
                  : "Загрузить фото обложки"}
            </Button>
            {coverPreview ? (
              <button
                type="button"
                className={aa.portalCoverRemoveBtn}
                onClick={removeCover}
                disabled={saving || coverUploading}
              >
                Убрать обложку — останется цветной фон
              </button>
            ) : (
              <p className={aa.hubHint}>
                Горизонтальное фото 3:2 лучше всего смотрится на главной. Без
                фото используется выбранный цвет ниже.
              </p>
            )}
          </div>
        </section>

        {gallerySection}

        <section className={aa.portalBrandBlock}>
          <p className={aa.hubSectionLabel}>Фирменный цвет</p>
          <p className={aa.hubHint}>
            Кнопки, вкладки и градиент шапки. С обложкой — акцент на элементах
            интерфейса.
          </p>
          <div className={aa.portalColorGrid}>
            {TRAINER_COLOR_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`${aa.portalColorChip} ${
                  color === preset.color ? aa.portalColorChipOn : ""
                }`}
                style={{ "--chip-color": preset.color }}
                onClick={() => saveColor(preset.color)}
                disabled={saving}
                aria-label={preset.label}
                title={preset.label}
              >
                <span className={aa.portalColorChipDot} aria-hidden />
              </button>
            ))}
          </div>
          <label className={aa.portalColorCustom}>
            <span>Свой цвет</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              onBlur={() => saveColor(color)}
            />
            <code>{color}</code>
          </label>
        </section>

        <section className={aa.portalBrandBlock}>
          <p className={aa.hubSectionLabel}>Шрифт</p>
          <div className={aa.portalFontGrid}>
            {PORTAL_FONT_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`${aa.portalFontBtn} ${
                  font === preset.id ? aa.portalFontBtnOn : ""
                }`}
                style={{ fontFamily: preset.family }}
                onClick={() => saveFont(preset.id)}
                disabled={saving}
              >
                {preset.label}
                <span className={aa.portalFontSample}>Аа Бб 123</span>
              </button>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`${s.trainerBrandPage} ${rootClassName}`.trim()}>
      <p className={s.trainerBrandHint}>
        Единый стиль клуба: шапка главной у админов и клиентов, фирменный цвет
        (вкладки и кнопки), шрифт. Изменения применяются сразу после сохранения.
      </p>

      <section className={s.trainerBrandSection}>
        <h3 className={s.docSectionTitle}>Аватар клуба</h3>
        <p className={s.trainerBrandHint}>
          Логотип клуба в шапке и навигации. Рекомендуем квадратное изображение.
        </p>
        {logoInput}
        <div className={aa.portalLogoRow}>
          <button
            type="button"
            className={aa.portalLogoTap}
            onClick={openLogoPicker}
            disabled={logoUploading}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="" className={aa.portalLogoImg} />
            ) : (
              <span className={aa.portalLogoPlaceholder}>+</span>
            )}
          </button>
          <div className={s.trainerBrandCoverActions}>
            <Button type="button" onClick={openLogoPicker} disabled={logoUploading}>
              {logoUploading
                ? "Загрузка…"
                : logoPreview
                  ? "Заменить"
                  : "Загрузить аватар"}
            </Button>
            {logoPreview ? (
              <Button
                type="button"
                variant="ghost"
                onClick={removeLogo}
                disabled={saving}
              >
                Убрать
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      <section className={s.trainerBrandSection}>
        <h3 className={s.docSectionTitle}>Обложка главной</h3>
        <p className={s.trainerBrandHint}>
          Фото вместо цветного градиента. Рекомендуем горизонтальное изображение 3:2.
        </p>
        {coverInput}
        <div
          className={`${s.trainerBrandCoverPreview} ${aa.portalBrandPreview}`}
          style={{ ...previewStyle, fontFamily: previewTheme.fontFamily }}
          role="presentation"
        >
          <div className={aa.portalBrandPreviewInner}>
            <span className={aa.portalBrandPreviewDate}>Сегодня</span>
            <strong className={aa.portalBrandPreviewTitle}>Привет!</strong>
            <span className={aa.portalBrandPreviewClub}>Ваш клуб</span>
          </div>
        </div>
        <div className={s.trainerBrandCoverActions}>
          <Button type="button" onClick={openCoverPicker} disabled={coverUploading}>
            {coverUploading
              ? "Загрузка…"
              : coverPreview
                ? "Заменить фото"
                : "Загрузить обложку"}
          </Button>
          {coverPreview ? (
            <Button
              type="button"
              variant="ghost"
              onClick={removeCover}
              disabled={saving}
            >
              Убрать
            </Button>
          ) : null}
        </div>
      </section>

      {gallerySection}

      <section className={s.trainerBrandSection}>
        <h3 className={s.docSectionTitle}>Фирменный цвет</h3>
        <p className={s.trainerBrandHint}>
          Вкладки, кнопки и градиент шапки (если нет фото-обложки).
        </p>
        <div className={s.trainerBrandColors}>
          {TRAINER_COLOR_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`${s.trainerBrandColorBtn} ${
                color === preset.color ? s.trainerBrandColorBtnOn : ""
              }`}
              style={{ "--brand-color": preset.color }}
              onClick={() => saveColor(preset.color)}
              disabled={saving}
            >
              <span className={s.trainerBrandColorDot} aria-hidden />
              {preset.label}
            </button>
          ))}
        </div>
        <div className={s.trainerBrandCustomColor}>
          <label htmlFor="club-portal-color">Свой цвет</label>
          <input
            id="club-portal-color"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            onBlur={() => saveColor(color)}
          />
          <code>{color}</code>
        </div>
      </section>

      <section className={s.trainerBrandSection}>
        <h3 className={s.docSectionTitle}>Шрифт приложения</h3>
        <p className={s.trainerBrandHint}>Текст на главной и в интерфейсе клиентского портала.</p>
        <div className={aa.portalFontGrid}>
          {PORTAL_FONT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`${aa.portalFontBtn} ${
                font === preset.id ? aa.portalFontBtnOn : ""
              }`}
              style={{ fontFamily: preset.family }}
              onClick={() => saveFont(preset.id)}
              disabled={saving}
            >
              {preset.label}
              <span className={aa.portalFontSample}>Аа Бб 123</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
